import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuthStore } from '../state/authStore';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Check, Loader2, Sparkles, ExternalLink } from 'lucide-react';
import PageHeader from '../components/layout/PageHeader';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/billing`;

function formatPrice(inr) {
  if (inr === 0) return 'Free';
  if (inr >= 100000) return `₹${(inr / 100000).toFixed(2)} L`;
  return `₹${inr.toLocaleString('en-IN')}`;
}

function FeatureRow({ label, value }) {
  let pretty = value;
  if (value === -1) pretty = 'Unlimited';
  else if (value === true) pretty = '✓';
  else if (value === false) pretty = '—';
  return (
    <div className="flex items-center justify-between py-1.5 text-xs border-b border-border/30 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{pretty}</span>
    </div>
  );
}

const FEATURE_LABELS = {
  ai_chat_per_day: 'AI chat messages / day',
  pitch_reports_per_month: 'Pitch reports / month',
  pitch_report_scope: 'Pitch report scope',
  watchlist_max: 'Watchlist size',
  alert_rules_max: 'Alert rules',
  broker_sync: 'Broker sync',
  daily_digest: 'Daily email digest',
  api_calls_per_month: 'API calls / month',
  export_excel: 'Export to Excel',
  priority_queue: 'Priority generation',
  seats: 'Team seats',
  clients_max: 'Clients',
  daily_intelligence: 'Daily client intelligence',
  team_chat: 'Team chat',
  audit_log: 'SEBI audit log',
  api_access: 'API access',
  custom_branding: 'Custom branding',
  priority_support: 'Priority support',
  custom_slas: 'Custom SLAs',
  whitelabel: 'Whitelabel',
  single_tenant_option: 'Single-tenant option',
};

export default function BillingPage() {
  const user = useAuthStore((s) => s.user);
  const userType = user?.userType || 'retail';
  const [plans, setPlans] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const refresh = async () => {
    try {
      const [plansResp, meResp] = await Promise.all([
        axios.get(`${API}/plans`, { params: { audience: userType } }),
        axios.get(`${API}/me`),
      ]);
      setPlans(plansResp.data || []);
      setMe(meResp.data || null);
    } catch { /* noop */ }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, [userType]);

  const currentPlanCode = me?.subscription?.plan?.code;
  const usage = useMemo(() => me?.usage || [], [me]);

  const handleUpgrade = async (planCode) => {
    setBusy(planCode);
    try {
      const { data } = await axios.post(`${API}/checkout`, { plan_code: planCode });
      if (data?.short_url) {
        window.location.href = data.short_url;
        return;
      }
      if (data?.ok || data?.dev_mode) {
        await refresh();
      }
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || 'Could not start checkout.');
    }
    setBusy('');
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel your subscription at the end of the current billing period?')) return;
    try {
      await axios.post(`${API}/cancel`);
      await refresh();
    } catch { /* noop */ }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <PageHeader
        title="Billing"
        description={userType === 'institutional' ? 'KubAIr Advisor OS subscription' : 'KubAIr Research subscription'}
      />

      {loading && <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}

      {me?.subscription && (
        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-xs text-muted-foreground">Current plan</p>
                <p className="text-lg font-bold flex items-center gap-2">
                  {me.subscription.plan.name}
                  <Badge className="bg-emerald-500/10 text-emerald-600 text-[10px]">{me.subscription.status}</Badge>
                </p>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold">{formatPrice(me.subscription.plan.price_inr || 0)}
                  <span className="text-xs text-muted-foreground font-normal">/period</span>
                </p>
                {me.subscription.current_period_end && (
                  <p className="text-[11px] text-muted-foreground">
                    Renews {new Date(me.subscription.current_period_end).toLocaleDateString('en-IN')}
                  </p>
                )}
              </div>
            </div>
            {me.subscription.cancel_at && (
              <div className="text-xs text-amber-600">
                Scheduled to end on {new Date(me.subscription.cancel_at).toLocaleDateString('en-IN')}
              </div>
            )}
            {me.subscription.status === 'active' && currentPlanCode !== 'retail_free' && (
              <Button size="sm" variant="outline" className="text-xs" onClick={handleCancel}>
                Cancel subscription
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Usage */}
      {usage.length > 0 && (
        <Card>
          <CardContent className="p-5 space-y-2">
            <p className="text-sm font-semibold">Usage (current period)</p>
            <div className="space-y-1">
              {usage.slice(0, 6).map((u, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{u.metric} · {u.period}</span>
                  <span className="font-medium">
                    {u.count}{u.limit != null && u.limit !== -1 ? ` / ${u.limit}` : ''}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plan cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {plans.map((p) => {
          const active = p.code === currentPlanCode;
          return (
            <Card key={p.code} className={active ? 'border-emerald-500/50 bg-emerald-500/[0.02]' : ''}>
              <CardContent className="p-5 space-y-3 flex flex-col h-full">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">{p.audience === 'retail' ? 'Research' : 'Advisor OS'}</p>
                    <h3 className="text-lg font-bold">{p.name}</h3>
                  </div>
                  {active && <Badge className="bg-emerald-500/10 text-emerald-600 text-[10px]"><Check className="w-3 h-3 mr-1" />Current</Badge>}
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold">{formatPrice(p.price_inr)}</span>
                  {p.billing_interval !== 'custom' && <span className="text-xs text-muted-foreground">/{p.billing_interval === 'annual' ? 'year' : 'month'}</span>}
                </div>
                {p.trial_days > 0 && !active && (
                  <p className="text-[11px] text-emerald-500">{p.trial_days}-day free trial</p>
                )}
                <div className="flex-1">
                  {Object.entries(p.features || {}).map(([k, v]) => (
                    <FeatureRow key={k} label={FEATURE_LABELS[k] || k} value={v} />
                  ))}
                </div>
                <Button
                  className={active ? '' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
                  variant={active ? 'outline' : 'default'}
                  disabled={active || !!busy}
                  onClick={() => handleUpgrade(p.code)}
                >
                  {busy === p.code ? <Loader2 className="w-4 h-4 animate-spin" /> : active ? 'Current plan' : (p.billing_interval === 'custom' ? 'Contact sales' : (
                    <>
                      <Sparkles className="w-4 h-4 mr-1.5" />
                      Upgrade
                    </>
                  ))}
                </Button>
                {p.billing_interval === 'custom' && (
                  <a href="mailto:sales@kubair.in?subject=KubAIr%20Advisor%20OS%20Enterprise"
                    className="text-[11px] text-center text-muted-foreground hover:text-foreground flex items-center justify-center gap-1">
                    sales@kubair.in <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <p className="text-[11px] text-muted-foreground">
        Prices in INR are inclusive of taxes unless marked otherwise. GST invoices issued for institutional plans (add GSTIN at checkout). Cancellation stops the next renewal; access continues until period end.
      </p>
    </div>
  );
}
